import { MetaAd, MetaAdAccount, MetaAdCampaign, MetaAdMetaAdCreative, MetaAdSet } from '@frontend/gql/social/graphql';

/**
 * Maps a MetaAdMetaAdCreative object to a simplified structure.
 */
export function mapMetaAdCreative(metaAdMetaAdCreative: MetaAdMetaAdCreative) {
  return {
    id: metaAdMetaAdCreative.id,
    createdAt: metaAdMetaAdCreative.createdAt,
    updatedAt: metaAdMetaAdCreative.updatedAt,
    adAccountId: metaAdMetaAdCreative.adAccountId,
    adCreativeId: metaAdMetaAdCreative.adCreativeId,
    instagramActorId: metaAdMetaAdCreative.instagramActorId ?? '',
    metaAdCreative: metaAdMetaAdCreative.metaAdCreative
      ? {
          id: metaAdMetaAdCreative.metaAdCreative.id,
          title: metaAdMetaAdCreative.metaAdCreative.title ?? '',
          body: metaAdMetaAdCreative.metaAdCreative.body ?? '',
          sourceInstagramMediaId: metaAdMetaAdCreative.metaAdCreative.sourceInstagramMediaId ?? '',
        }
      : {
          id: '',
          title: '',
          body: '',
          sourceInstagramMediaId: '',
        },
  };
}

/**
 * Maps a MetaAd object to a simplified structure including creatives.
 */
export function mapMetaAd(metaAd: MetaAd) {
  return {
    id: metaAd.id,
    name: metaAd.name,
    adAccountId: metaAd.adAccountId,
    status: metaAd.status,
    createdAt: metaAd.createdAt === null || metaAd.createdAt === undefined ? '' : metaAd.createdAt,
    updatedAt: metaAd.updatedAt === null || metaAd.updatedAt === undefined ? undefined : metaAd.updatedAt,
    creatives: (metaAd.metaAdMetaAdCreatives ?? [])
      .filter((c: MetaAdMetaAdCreative | null): c is MetaAdMetaAdCreative => !!c)
      .map(mapMetaAdCreative),
  };
}

/**
 * Maps a MetaAdSet object to a simplified structure including ads.
 */
export function mapMetaAdSet(metaAdSet: MetaAdSet) {
  return {
    id: metaAdSet.id,
    name: metaAdSet.name,
    adAccountId: metaAdSet.adAccountId,
    status: metaAdSet.status,
    startTime: metaAdSet.startTime,
    stopTime: metaAdSet.stopTime,
    dailyBudget: metaAdSet.dailyBudget,
    lifetimeBudget: metaAdSet.lifetimeBudget,
    bidAmount: metaAdSet.bidAmount,
    billingEvent: metaAdSet.billingEvent,
    ads: (metaAdSet.metaAds ?? []).filter((a: MetaAd | null): a is MetaAd => !!a).map(mapMetaAd),
  };
}

/**
 * Maps a MetaAdCampaign object to a simplified structure including ad sets.
 */
export function mapMetaAdCampaign(metaAdCampaign: MetaAdCampaign) {
  return {
    id: metaAdCampaign.id,
    name: metaAdCampaign.name,
    status: metaAdCampaign.status,
    startTime: metaAdCampaign.startTime,
    stopTime: metaAdCampaign.stopTime,
    adSets: (metaAdCampaign.metaAdSets ?? []).filter((s: MetaAdSet | null): s is MetaAdSet => !!s).map(mapMetaAdSet),
  };
}

/**
 * Maps a MetaAdAccount object to a simplified structure including campaigns.
 */
export function mapMetaAdAccount(metaAdAccount: MetaAdAccount) {
  return {
    id: metaAdAccount.id,
    name: metaAdAccount.name,
    status: metaAdAccount.status,
    createdAt: metaAdAccount.createdAt === null ? undefined : metaAdAccount.createdAt,
    updatedAt: metaAdAccount.updatedAt === null ? undefined : metaAdAccount.updatedAt,
    campaigns: (metaAdAccount.metaAdCampaigns ?? [])
      .filter((c: MetaAdCampaign | null): c is MetaAdCampaign => !!c)
      .map(mapMetaAdCampaign),
  };
}
